import { Logo } from '@syncanix/ui';

import { BRAND } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';

import type { ReactElement } from 'react';

/**
 * The central widget's chrome: its header, its composer, and the four corner
 * brackets that pop in around the wireframe.
 *
 * Hoisted out of `StoryWidget` as a leaf — nothing here reads anything from the
 * parent, so the parent is left as the transcript and the assembly order, which
 * is what it is actually about.
 */

/* ------------------------------------------------------------------ */

export function WidgetHeader(): ReactElement {
  const { t } = useT();
  return (
    <div className="relative flex flex-none items-center justify-between gap-2 border-b border-[rgba(17,24,39,.12)] px-[13px] py-[11px]">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className="relative inline-flex size-8 flex-none items-center justify-center rounded-full bg-[#f1f5f9]"
          style={{
            boxShadow: '0 0 0 1px rgba(15,23,42,.08), 0 4px 10px -3px rgba(37,99,235,.38)',
            animation: 'sxPop .6s cubic-bezier(.34,1.56,.64,1) 1.02s backwards',
          }}
        >
          <Logo size={26} />
          <span
            aria-hidden="true"
            className="absolute -bottom-px -end-px size-2 rounded-full bg-[#047857]"
            style={{
              boxShadow: '0 0 0 2px #fff',
              animation:
                'sxPopS .45s cubic-bezier(.34,1.56,.64,1) 1.34s backwards, bdPulse 2.6s ease-in-out 2.2s infinite',
            }}
          />
        </span>
        <span className="flex min-w-0 flex-col leading-tight">
          <span
            className="text-sm font-semibold text-[#111827]"
            style={{ animation: 'sxInL .5s cubic-bezier(.16,1,.3,1) 1.14s backwards' }}
          >
            {t('home.story.widget.author')}
          </span>
          <span
            className="text-[11.5px] text-[#5f6673]"
            style={{ animation: 'sxInL .5s cubic-bezier(.16,1,.3,1) 1.22s backwards' }}
          >
            {t('home.story.widget.status')}
          </span>
        </span>
      </div>
      <span
        aria-hidden="true"
        className="absolute inset-x-0 -bottom-px h-0.5 origin-[left_center] rtl:origin-[right_center]"
        style={{
          background: 'linear-gradient(90deg,var(--bd-cyan),var(--bd-blue) 50%,var(--bd-violet))',
          animation:
            'sxGrowX .6s cubic-bezier(.4,0,.2,1) 1.28s backwards, sxOut .5s ease 2.4s forwards',
        }}
      />
    </div>
  );
}

export function WidgetComposer(): ReactElement {
  const { t } = useT();
  return (
    <div
      className="flex-none border-t border-[rgba(17,24,39,.12)] px-[13px] py-[11px]"
      style={{ animation: 'sxUp .5s cubic-bezier(.16,1,.3,1) 1.38s backwards' }}
    >
      <div className="flex items-center gap-[7px] rounded-[13px] border border-[rgba(17,24,39,.12)] bg-white p-1.5 ps-2.5">
        <span className="relative flex h-[19px] min-w-0 flex-1 items-center gap-0.5">
          <span
            className="absolute top-px text-sm text-[#5f6673]"
            style={{
              insetInlineStart: 0,
              animation: 'sxOut .2s linear 2.95s backwards, sxFade .5s linear 4.3s backwards',
            }}
          >
            {t('home.story.widget.placeholder')}
          </span>
          <span
            data-type
            className="inline-block overflow-hidden whitespace-nowrap text-sm text-[#111827]"
            style={{
              animation: 'sxTypeW 1s steps(27,end) 3s backwards, sxOut .2s linear 4.02s forwards',
            }}
          >
            {t('home.story.widget.ask')}
          </span>
          <span
            aria-hidden="true"
            className="inline-block h-3.5 w-[1.5px] flex-none bg-[#2563eb]"
            style={{
              animation:
                'sxFade .1s linear 2.95s backwards, sxCaret .85s steps(1) 3s infinite, sxOut .18s linear 4.02s forwards',
            }}
          />
        </span>
        <span
          aria-hidden="true"
          className="grid size-7 flex-none place-items-center rounded-xl text-xs text-white rtl:-scale-x-100"
          style={{
            background: 'linear-gradient(155deg,#2563eb,#1d4ed8)',
            boxShadow: '0 4px 14px -6px rgba(37,99,235,.38)',
            animation:
              'sxPop .5s cubic-bezier(.34,1.56,.64,1) 1.5s backwards, sxFlash .75s ease-out 4s backwards',
          }}
        >
          ↑
        </span>
      </div>
    </div>
  );
}

/**
 * The four corner brackets that pop in around the wireframe. Authored as data
 * because they differ only by which two borders they carry and which corner
 * they round — four near-identical JSX blocks would invite one to drift.
 */
export const CORNERS = [
  {
    key: 'ts',
    color: BRAND.cyan,
    sides: ['Left', 'Top'],
    radius: '6px 0 0 0',
    pos: { insetInlineStart: -8, top: -8 },
    at: 0.35,
  },
  {
    key: 'te',
    color: BRAND.blue,
    sides: ['Right', 'Top'],
    radius: '0 6px 0 0',
    pos: { insetInlineEnd: -8, top: -8 },
    at: 0.41,
  },
  {
    key: 'be',
    color: BRAND.violet,
    sides: ['Right', 'Bottom'],
    radius: '0 0 6px 0',
    pos: { insetInlineEnd: -8, bottom: -8 },
    at: 0.47,
  },
  {
    key: 'bs',
    color: BRAND.purple,
    sides: ['Left', 'Bottom'],
    radius: '0 0 0 6px',
    pos: { insetInlineStart: -8, bottom: -8 },
    at: 0.53,
  },
].map((corner) => ({
  key: corner.key,
  style: {
    ...corner.pos,
    width: 20,
    height: 20,
    borderRadius: corner.radius,
    [`border${corner.sides[0]}`]: `2px solid ${corner.color}`,
    [`border${corner.sides[1]}`]: `2px solid ${corner.color}`,
    animation: `sxPop .5s cubic-bezier(.34,1.56,.64,1) ${corner.at}s backwards, sxOut .5s ease ${corner.at + 2.15}s forwards`,
  },
}));
